import React, { useState, useEffect, useRef } from "react";
import { Select, Collapse } from "antd";
import { getPharmacyItems, getPharmacyItemById } from "../../../../apis/pharmacyItems";
import { createPOSSale, updatePOSDeliveryStatus } from "../../../../apis/pos";
import { getProductBundles, getProductBundleById } from "../../../../apis/productBundles";
import { PharmacyItem } from "../../../../interfaces/PharmacyItems";
import { ProductBundle } from "../../../../interfaces/ProductBundle";
import { useNotification } from "../../../../NotificationContext";
import { Visit } from "../../../../interfaces/Visit";
import { camelToSnake } from "../../../../utils/CamelToSnake";
import { getVisitEntries } from "../../../../apis/visit";
const { Option } = Select;

interface PharmacyRequestFormProps {
  initialData?: any;
  visit: Visit;
  onSave: (data: any) => void;
  onCancel: () => void;
}

interface RequestItem {
  pharmacyItemId: number;
  name: string;
  quantity: number;
  status: string;
  batchId?: number;
  mrp?: number;
  sellingPrice?: number; // taxable base price (before tax)
  taxPercentage?: number;
}

const PharmacyRequestForm: React.FC<PharmacyRequestFormProps> = ({
  initialData,
  visit,
  onSave,
  onCancel,
}) => {
  const { showNotification } = useNotification();
  const [pharmacyItems, setPharmacyItems] = useState<PharmacyItem[]>([]);
  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [loading, setLoading] = useState(false);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSubmittingRef = useRef(false);
  const [existingRequests, setExistingRequests] = useState<any[]>([]);
  const [existingLoading, setExistingLoading] = useState(false);
  const [updatingDeliveryId, setUpdatingDeliveryId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    items: (initialData?.items || []) as RequestItem[],
  });
  const [newItem, setNewItem] = useState<RequestItem>({
    pharmacyItemId: 0,
    name: "",
    quantity: 1,
    status: "pending",
  });
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);

  useEffect(() => {
    fetchPharmacyItems();
    fetchBundles();
  }, []);

  useEffect(() => {
    if (!visit?.id) return;

    const fetchExistingRequests = async () => {
      setExistingLoading(true);
      try {
        const res: any = await getVisitEntries(visit.id, {
          entry_type: "pharmacy_request",
        });
        const rawEntries = res?.data ?? res?.entries ?? res ?? [];
        const requests =
          Array.isArray(rawEntries)
            ? rawEntries
                .map((entry: any) => {
                  const data = entry.formData || entry.form_data || {};
                  const pharmacyRequest =
                    data.pharmacy_request || data.pharmacyRequest;
                  if (!pharmacyRequest) return null;
                  return {
                    ...pharmacyRequest,
                    entryId: entry.id,
                    data,
                  };
                })
                .filter(Boolean)
            : [];
        setExistingRequests(requests as any[]);
      } catch (error) {
        console.error("Failed to fetch existing pharmacy requests for visit:", error);
      } finally {
        setExistingLoading(false);
      }
    };

    fetchExistingRequests();
  }, [visit?.id]);

  const fetchBundles = async () => {
    try {
      const res: any = await getProductBundles();
      const data = Array.isArray(res) ? res : res?.data ?? [];
      setBundles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch bundles:", err);
    }
  };

  const fetchPharmacyItems = async () => {
    try {
      setLoading(true);
      const response: any = await getPharmacyItems({ per_page: 1000, is_active: true, has_batches: true });
      // Handle different response structures: could be array, or { data: [...], links: {...}, meta: {...} }
      const items = Array.isArray(response) ? response : response?.data || [];
      setPharmacyItems(items || []);
    } catch (error) {
      console.error("Failed to fetch pharmacy items:", error);
      showNotification(
        "error",
        "Error",
        "Failed to fetch pharmacy items",
        "topRight"
      );
    } finally {
      setLoading(false);
    }
  };

  const getBatchWithNearestExpiry = (item: PharmacyItem): { id: number; batch_number?: string; mrp: number; tax_percentage: number; selling_price: string } | null => {
    const raw = item?.batches || [];
    const usable = raw
      .map((batch: any) => {
        const availQty = batch.availableQuantity ?? batch.available_quantity ?? batch.current_quantity ?? batch.currentQuantity ?? 0;
        return {
          id: batch.id ?? batch.pharmacy_batch_id,
          batch_number: batch.batchNumber ?? batch.batch_number,
          expiry_date: batch.expiryDate ?? batch.expiry_date,
          selling_price: batch.sellingPrice?.toString() ?? batch.selling_price ?? "0",
          available_quantity: availQty,
          is_expired: batch.isExpired ?? batch.is_expired ?? false,
          is_active: batch.isActive ?? batch.is_active ?? true,
          mrp: parseFloat(batch.sellingPrice ?? batch.selling_price ?? batch.purchasePrice ?? batch.purchase_price ?? "0"),
          tax_percentage: batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage ?? 0,
        };
      })
      .filter((b: any) => (b.available_quantity ?? 1) > 0 && b.is_active && !b.is_expired);
    if (usable.length === 0) return null;
    usable.sort((a: any, b: any) => {
      const dateA = a.expiry_date ? new Date(a.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = b.expiry_date ? new Date(b.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
      return dateA - dateB;
    });
    const b = usable[0];
    return { id: b.id, batch_number: b.batch_number, mrp: b.mrp, tax_percentage: b.tax_percentage ?? 0, selling_price: b.selling_price ?? "0" };
  };

  const getTaxableFromMrp = (mrp: number, taxPct: number): number => {
    if (!taxPct || !Number.isFinite(taxPct)) return mrp;
    return mrp / (1 + taxPct / 100);
  };

  const buildRequestItemsFromBundle = (bundle: ProductBundle, pharmacyItemsList: PharmacyItem[]): RequestItem[] => {
    const items: RequestItem[] = [];
    (bundle.items ?? []).forEach((bi: any) => {
      const itemId = bi.pharmacy_item_id ?? bi.pharmacyItemId ?? bi.pharmacy_item?.id ?? bi.pharmacyItem?.id;
      const pharmacyItem = pharmacyItemsList.find((p) => p.id === itemId) ?? bi.pharmacy_item ?? bi.pharmacyItem;
      const qty = Math.max(1, bi.quantity ?? bi.sort_order ?? 1);
      if (!pharmacyItem?.id) return;

      const batch = getBatchWithNearestExpiry(pharmacyItem);
      if (!batch) {
        showNotification("warning", "No batch", `No usable batch for ${pharmacyItem.name ?? "item"}. Skipping.`, "topRight");
        return;
      }
      const taxPct = batch.tax_percentage ?? 0;
      const sellingPrice = taxPct > 0 ? getTaxableFromMrp(batch.mrp, taxPct) : batch.mrp;
      items.push({
        pharmacyItemId: pharmacyItem.id,
        name: pharmacyItem.name ?? "Item",
        quantity: qty,
        status: "pending",
        batchId: batch.id,
        mrp: batch.mrp,
        sellingPrice,
        taxPercentage: taxPct,
      });
    });
    return items;
  };

  const handleBundleSelect = async (bundleId: number | null) => {
    if (!bundleId) return;
    setBundleLoading(true);
    try {
      const res: any = await getProductBundleById(bundleId);
      const b = res?.data ?? res ?? null;
      if (!b?.items?.length) {
        showNotification("warning", "Empty bundle", "This bundle has no items.", "topRight");
        return;
      }
      const itemsWithBatches: PharmacyItem[] = [...pharmacyItems];
      for (const bi of b.items) {
        const itemId = bi.pharmacy_item_id ?? bi.pharmacyItemId ?? bi.pharmacy_item?.id ?? bi.pharmacyItem?.id;
        if (!itemId) continue;
        const fromList = pharmacyItems.find((p) => p.id === itemId);
        const fromBundle = bi.pharmacy_item ?? bi.pharmacyItem;
        const hasBatches = (batches: any[]) => Array.isArray(batches) && batches.length > 0;
        const listHasBatches = fromList?.batches && hasBatches(fromList.batches);
        const bundleHasBatches = fromBundle?.batches && hasBatches(fromBundle.batches);
        if (!listHasBatches && !bundleHasBatches) {
          try {
            const fetched: any = await getPharmacyItemById(itemId);
            const fullItem = fetched?.data ?? fetched;
            const batches = fullItem?.batches ?? [];
            if (fullItem && (fullItem.id ?? fullItem.pharmacy_item_id)) {
              const resolved = { ...(fromList || fromBundle || {}), ...fullItem, id: fullItem.id ?? fullItem.pharmacy_item_id ?? itemId, batches };
              const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
              if (idx >= 0) itemsWithBatches[idx] = resolved;
              else itemsWithBatches.push(resolved);
            }
          } catch (_) {
            if (fromBundle) {
              const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
              const merged = { ...(fromList || {}), ...fromBundle, id: itemId };
              if (idx >= 0) itemsWithBatches[idx] = merged;
              else itemsWithBatches.push(merged);
            }
          }
        } else if (fromBundle?.batches && !listHasBatches) {
          const merged = { ...(fromList || {}), ...fromBundle, id: itemId };
          const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
          if (idx >= 0) itemsWithBatches[idx] = merged;
          else itemsWithBatches.push(merged);
        }
      }
      const requestItems = buildRequestItemsFromBundle(b, itemsWithBatches);
      setFormData((prev) => {
        let items = [...prev.items];
        for (const item of requestItems) {
          const existingIndex = items.findIndex(
            (i) => i.pharmacyItemId === item.pharmacyItemId && i.batchId === item.batchId
          );
          if (existingIndex >= 0) {
            items[existingIndex] = {
              ...items[existingIndex],
              quantity: items[existingIndex].quantity + item.quantity,
            };
          } else {
            items.push({ ...item });
          }
        }
        return { ...prev, items };
      });
      showNotification("success", "Bundle added", `Added ${requestItems.length} item(s) from bundle.`, "topRight");
    } catch (err: any) {
      console.error("Failed to add bundle:", err);
      showNotification("error", "Error", err?.message ?? "Failed to add bundle", "topRight");
    } finally {
      setBundleLoading(false);
    }
  };

  const addOrMergeItem = (item: RequestItem) => {
    if (!item.pharmacyItemId || item.quantity <= 0 || !item.batchId) return;
    setFormData((prev) => {
      const existingIndex = prev.items.findIndex(
        (i) => i.pharmacyItemId === item.pharmacyItemId && i.batchId === item.batchId
      );
      if (existingIndex >= 0) {
        const updatedItems = [...prev.items];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + item.quantity,
        };
        return { ...prev, items: updatedItems };
      }
      return { ...prev, items: [...prev.items, { ...item }] };
    });
  };

  const handleExistingDeliveryUpdate = async (
    saleId: number,
    nextStatus: "received" | "completed"
  ) => {
    if (!saleId) return;
    try {
      setUpdatingDeliveryId(saleId);
      await updatePOSDeliveryStatus(saleId, nextStatus);
      showNotification(
        "success",
        "Delivery status updated",
        "",
        "topRight"
      );
      setExistingRequests((prev) =>
        prev.map((sale: any) =>
          (sale.id ?? sale.sale_id ?? sale.saleId) === saleId
            ? {
                ...sale,
                deliveryStatus: nextStatus,
                delivery_status: nextStatus,
              }
            : sale
        )
      );
    } catch (error) {
      console.error("Failed to update delivery status:", error);
      showNotification(
        "error",
        "Failed to update delivery status",
        "",
        "topRight"
      );
    } finally {
      setUpdatingDeliveryId(null);
    }
  };

  const handlePharmacyItemSelect = (pharmacyItemId: number) => {
    const selectedItem = pharmacyItems.find((item) => item.id === pharmacyItemId);

    if (selectedItem) {
      const batches = selectedItem.batches || [];

      // Find first available batch (not expired, has quantity, and is active)
      const availableBatch = batches.find(
        (batch: any) => {
          const isNotExpired = !(batch.isExpired ?? batch.is_expired);
          const availableQty = batch.availableQuantity ?? batch.available_quantity ?? 0;
          const hasQuantity = Number(availableQty) > 0;
          const isActive = (batch.isActive ?? batch.is_active) !== false && (batch.isActive ?? batch.is_active) !== 0;
          return isNotExpired && hasQuantity && isActive;
        }
      );

      if (!availableBatch) {
        showNotification(
          "warning",
          "No Available Batch",
          `Item "${selectedItem.name}" does not have an available batch. Please select another item.`,
          "topRight"
        );
        // Reset the select to prevent adding item without batch
        setNewItem({
          pharmacyItemId: 0,
          name: "",
          quantity: 1,
          status: "pending",
          batchId: undefined,
          sellingPrice: undefined,
        });
        return;
      }

      const batchId = availableBatch.id;
      // Batch selling_price/MRP is typically tax-inclusive (e.g. ₹178)
      const mrpStr = (availableBatch as any).sellingPrice ?? (availableBatch as any).selling_price ?? (availableBatch as any).mrp ?? "0";
      const mrp = parseFloat(String(mrpStr)) || 0;
      const taxPct = Number((availableBatch as any).taxPercentage ?? (availableBatch as any).tax_percentage ?? (availableBatch as any).grn_item?.taxPercentage ?? (availableBatch as any).grn_item?.tax_percentage ?? 0) || 0;
      // Taxable base = MRP / (1 + tax%) e.g. 178 / 1.12 = 158.93
      const sellingPrice = taxPct > 0 ? mrp / (1 + taxPct / 100) : mrp;

      const itemToAdd: RequestItem = {
        pharmacyItemId: selectedItem.id!,
        name: selectedItem.name,
        quantity: newItem.quantity || 1,
        status: "pending",
        batchId,
        mrp,
        sellingPrice,
        taxPercentage: taxPct,
      };
      addOrMergeItem(itemToAdd);
      setNewItem({
        pharmacyItemId: 0,
        name: "",
        quantity: 1,
        status: "pending",
        batchId: undefined,
        sellingPrice: undefined,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (isSubmittingRef.current || saving) {
      return;
    }

    if (formData.items.length === 0) {
      showNotification(
        "warning",
        "Validation Error",
        "Please add at least one item",
        "topRight"
      );
      return;
    }

    isSubmittingRef.current = true;
    setSaving(true);

    try {
      // Get current user from localStorage
      const user = JSON.parse(window.localStorage.getItem("user") || "{}");
      const userId = user?.id;

      if (!userId) {
        showNotification(
          "error",
          "Error",
          "User not found. Please login again.",
          "topRight"
        );
        setSaving(false);
        return;
      }

      // Get patient ID from visit
      const patientId = visit.patientId || visit.patient?.id;
      if (!patientId) {
        showNotification(
          "error",
          "Error",
          "Patient ID not found in visit",
          "topRight"
        );
        setSaving(false);
        return;
      }

      // Get current date
      const saleDate = new Date().toISOString().split("T")[0];

      // Validate that all items have batch IDs
      const itemsWithoutBatch = formData.items.filter((item) => !item.batchId);
      if (itemsWithoutBatch.length > 0) {
        showNotification(
          "error",
          "Validation Error",
          "Some items are missing batch information. Please remove and re-add them.",
          "topRight"
        );
        setSaving(false);
        return;
      }

      // Convert request items to POS sale items format (camelCase - converted to snake_case at API)
      const saleItems = formData.items.map((item) => {
        if (!item.batchId) {
          throw new Error(`Item ${item.name} is missing batch ID`);
        }
        const taxableBase = Number(item.sellingPrice) || 0;
        const taxPct = Number(item.taxPercentage) || 0;
        const qty = Number(item.quantity) || 1;
        const itemTaxableAmount = taxableBase * qty;
        const itemTaxAmount = taxPct > 0 ? itemTaxableAmount * (taxPct / 100) : 0;
        const itemNetAmount = itemTaxableAmount + itemTaxAmount;
        return {
          pharmacyItemId: item.pharmacyItemId,
          pharmacyBatchId: item.batchId,
          quantity: qty,
          mrp: item.mrp || 0,
          sellingPrice: taxableBase,
          discountPercentage: 0,
          taxPercentage: taxPct,
          taxAmount: Number(itemTaxAmount.toFixed(2)),
          netAmount: Number(itemNetAmount.toFixed(2)),
        };
      });

      const saleData = {
        patientId,
        visitId: visit.id || null,
        doctorId: userId,
        saleDate,
        discountPercentage: 0,
        status: "pending",
        deliveryStatus: "pending",
        notes: "Pharmacy Request from IPD",
        items: saleItems,
      };

      const response: any = await createPOSSale(camelToSnake(saleData));
      const pharmacySaleId = response?.data?.id || response?.data?.data?.id || response?.id;

      if (isSubmittingRef.current) {
        onSave({
          pharmacySaleId,
          notes: "Pharmacy Request from IPD",
        });
      }
    } catch (error: any) {
      console.error("Failed to save pharmacy request:", error);
      const errorMessage =
        error?.response?.data?.message || "Failed to save pharmacy request";
      showNotification("error", "Error", errorMessage, "topRight");
    } finally {
      isSubmittingRef.current = false;
      setSaving(false);
    }
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_: RequestItem, i: number) => i !== index),
    });
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      quantity: Math.max(1, quantity),
    };
    setFormData({
      ...formData,
      items: updatedItems,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <div>

        <div className="flex gap-2">
          <div>

            <label className="block text-sm font-medium text-gray-700">
              Select Bundle
            </label>
            <Select
              // style={{ width: "100%" }}
              placeholder="Add Bundle (adds all items)"
              value={selectedBundleId ?? undefined}
              onChange={(val) => {
                if (val) {
                  handleBundleSelect(val);
                  setSelectedBundleId(null);
                }
              }}
              loading={bundleLoading}
              allowClear
              className="col-span-2"
            >
              {bundles
                .filter((b) => b.isActive ?? b.is_active ?? true)
                .map((b) => (
                  <Option key={b.id} value={b.id}>
                    {b.name} {b.code && `(${b.code})`}
                  </Option>
                ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Select Pharmacy Item
            </label>
            <Select
              // style={{ width: "100%" }}
              placeholder="Select Pharmacy Item (adds to list)"
              value={newItem.pharmacyItemId && newItem.batchId ? newItem.pharmacyItemId : undefined}
              onChange={handlePharmacyItemSelect}
              loading={loading}
              showSearch
              filterOption={(input, option) => {
                const children = option?.children;
                const text = typeof children === 'string' ? children : String(children);
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              className="col-span-2"
              allowClear
            >
              {pharmacyItems.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name} {item.form && `(${item.form})`}
                  {item.strength && ` - ${item.strength}`}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {formData.items.length > 0 && (
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                    Item Name
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                    Quantity
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formData.items.map((item: RequestItem, index: number) => (
                  <tr key={index}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItemQuantity(
                            index,
                            parseInt(e.target.value) || 1
                          )
                        }
                        className="px-2 py-1 w-20 text-sm rounded-md border border-gray-300"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full">
                        {item.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {visit?.id && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Existing Pharmacy Requests
            </h3>
            {existingLoading ? (
              <p className="mt-2 text-xs text-gray-500">
                Loading pharmacy requests...
              </p>
            ) : existingRequests.length === 0 ? (
              <p className="mt-2 text-xs text-gray-500">
                No pharmacy requests have been created for this visit yet.
              </p>
            ) : (
              <div className="mt-2">
                <Collapse
                  accordion={false}
                  items={existingRequests.map((sale: any) => {
                    const saleId =
                      sale.id ??
                      sale.sale_id ??
                      sale.saleId;
                    const saleNumber =
                      sale.saleNumber ??
                      sale.sale_number ??
                      (saleId ? `#${saleId}` : "-");
                    const rawDate =
                      sale.saleDate ??
                      sale.sale_date 
                    const deliveryStatus =
                      sale.deliveryStatus ??
                      sale.delivery_status ??
                      "pending";
                    const normalizedDelivery =
                      typeof deliveryStatus === "string"
                        ? deliveryStatus.toLowerCase()
                        : "";
                    const status = sale.status ?? "pending";
                    const items = sale.items ?? [];
                    const notes = sale.notes ?? sale.data?.notes;

                    return {
                      key: String(saleId ?? sale.entryId ?? saleNumber),
                      label: (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="text-sm font-medium text-gray-900">
                            {saleNumber}
                          </span>
                          <span className="text-sm text-gray-600">
                            {rawDate
                              ? new Date(rawDate).toLocaleString()
                              : "-"}
                          </span>
                          <span className="text-sm text-gray-600 capitalize">
                            {deliveryStatus || "-"}
                          </span>
                          {saleId && normalizedDelivery === "dispatched" ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExistingDeliveryUpdate(saleId, "received");
                              }}
                              disabled={updatingDeliveryId === saleId}
                              className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50 text-sm font-medium"
                            >
                              {updatingDeliveryId === saleId
                                ? "Updating..."
                                : "Mark as Received"}
                            </button>
                          ) : null}
                        </div>
                      ),
                      children: (
                        <div className="space-y-3 pt-1">
                          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase">
                                Sale Number
                              </div>
                              <div className="mt-0.5 text-sm text-gray-800">
                                {saleNumber}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase">
                                Date
                              </div>
                              <div className="mt-0.5 text-sm text-gray-800">
                                {rawDate
                                  ? new Date(rawDate).toLocaleString()
                                  : "-"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase">
                                Payment Status
                              </div>
                              <div className="mt-0.5 text-sm text-gray-800 capitalize">
                                {status || "-"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase">
                                Delivery Status
                              </div>
                              <div className="mt-0.5 text-sm text-gray-800 capitalize">
                                {deliveryStatus || "-"}
                              </div>
                            </div>
                          </div>
                          {items && items.length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                                Items
                              </div>
                              <ul className="text-sm text-gray-800 list-disc list-inside">
                                {items.map((item: any, idx: number) => {
                                  const name =
                                    item.name ??
                                    item.pharmacy_item?.name ??
                                    item.pharmacyItem?.name ??
                                    "-";
                                  const quantity = item.quantity ?? "-";
                                  return (
                                    <li key={item.id ?? idx}>
                                      {quantity} x {name}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          {notes && (
                            <div className="pt-2 border-t border-gray-100">
                              <div className="text-xs font-medium text-gray-500 uppercase">
                                Notes
                              </div>
                              <div className="mt-0.5 text-sm text-gray-800">
                                {notes}
                              </div>
                            </div>
                          )}
                        </div>
                      ),
                    };
                  })}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || formData.items.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Request"}
        </button>
      </div>
    </form>
  );
};

export default PharmacyRequestForm;
